import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../../config/axiosConfig';
import {
    TextField,
    InputAdornment,
    Paper,
    List,
    ListItem,
    Typography,
    Box,
    CircularProgress,
    ClickAwayListener,
    Portal
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const GenericSearchBar = ({
    onResultSelect,
    maxSelections = Infinity,
    currentSelections = [],
    searchEndpoint,
    placeholder = "Search...",
    queryParam = "query",
    renderResultItem,
    disabled = false,
    hideSearch = false,
    getResultValue = (result) => result.id || result.symbol || result.value,
    getResultKey = (result) => `${getResultValue(result)}-${result.type || 'item'}`,
    minQueryLength = 1,
    filterResults = (results, currentSelections) => {
        return results.filter(result =>
            !currentSelections.includes(getResultValue(result))
        );
    },

}) => {
    const theme = useTheme();
    const [query, setQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [results, setResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isSelecting, setIsSelecting] = useState(false);
    const searchRef = useRef(null);
    const lastQueryRef = useRef('');
    const portalContainerRef = useRef(null);

    // Create a portal container if it doesn't exist
    useEffect(() => {
        if (!portalContainerRef.current) {
            // Create a div for the portal in the document body
            portalContainerRef.current = document.createElement('div');
            portalContainerRef.current.id = 'search-dropdown-portal';
            portalContainerRef.current.style.position = 'absolute';
            portalContainerRef.current.style.zIndex = '9999999'; // Very high z-index
            document.body.appendChild(portalContainerRef.current);
        }

        return () => {
            // Clean up the portal container on unmount
            if (portalContainerRef.current && document.body.contains(portalContainerRef.current)) {
                document.body.removeChild(portalContainerRef.current);
            }
        };
    }, []);

    // Update portal position when search input changes position
    useEffect(() => {
        const updatePortalPosition = () => {
            if (searchRef.current && portalContainerRef.current) {
                const rect = searchRef.current.getBoundingClientRect();
                portalContainerRef.current.style.top = `${rect.bottom + window.scrollY}px`;
                portalContainerRef.current.style.left = `${rect.left + window.scrollX}px`;
                portalContainerRef.current.style.width = `${rect.width}px`;
            }
        };

        updatePortalPosition();
        window.addEventListener('resize', updatePortalPosition);
        window.addEventListener('scroll', updatePortalPosition);

        return () => {
            window.removeEventListener('resize', updatePortalPosition);
            window.removeEventListener('scroll', updatePortalPosition);
        };
    }, [showDropdown]);

    // Memoize the filterResults function to prevent it from changing on every render
    const memoizedFilterResults = useCallback(
        (results, currentSelections) => filterResults(results, currentSelections),
        [filterResults]
    );

    useEffect(() => {
        // Don't fetch if component is disabled, query is too short, or the same as last query
        if (disabled || hideSearch || query.length < minQueryLength || query === lastQueryRef.current) {
            return;
        }

        // Store the current query for comparison
        lastQueryRef.current = query;

        const fetchResults = async () => {
            setIsLoading(true);
            try {
                const response = await axios.get(searchEndpoint, {
                    params: { [queryParam]: query }
                });

                // Only update results if the query hasn't changed while fetching
                if (query === lastQueryRef.current) {
                    // Filter out already selected items
                    const filteredResults = memoizedFilterResults(response.data, currentSelections);
                    setResults(filteredResults);
                    setShowDropdown(true);
                }
            } catch (error) {
                console.error(`Error fetching search results from ${searchEndpoint}:`, error);
                setResults([]);
            } finally {
                setIsLoading(false);
            }
        };

        const timeoutId = setTimeout(fetchResults, 500);
        return () => clearTimeout(timeoutId);
    }, [query, currentSelections, searchEndpoint, queryParam, memoizedFilterResults, minQueryLength, disabled, hideSearch]);

    // Handle selecting a result - pass the full result object
    const handleResultClick = (result) => {
        setIsSelecting(true);
        const value = getResultValue(result);

        // Close dropdown first, then clear state
        setShowDropdown(false);
        setResults([]);
        setQuery('');

        // Call selection callback with both the value and the full result
        setTimeout(() => {
            onResultSelect(value, result);
            setIsSelecting(false);
        }, 100);
    };

    // Use ClickAwayListener for better control
    const handleClickAway = () => {
        if (!isSelecting) {
            setShowDropdown(false);
            setQuery('');
        }
    };

    // Handle input focus
    const handleInputFocus = () => {
        // Show existing results if we have them and query is not empty
        if (results.length > 0 && query.length >= minQueryLength) {
            setShowDropdown(true);
        }
    };

    const isSelectionsAvailable = () => {
        return currentSelections.length < maxSelections;
    };

    // Common dropdown paper styles
    const dropdownPaperStyle = {
        width: '100%',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        backdropFilter: 'blur(8px)',
        backgroundColor: theme.palette.mode === 'dark'
            ? 'rgba(15, 15, 15, 0.98)'
            : 'rgba(255, 255, 255, 0.98)',
        marginTop: '4px', // Small gap between input and dropdown
        borderRadius: '8px',
        overflow: 'hidden'
    };

    // Default result item renderer if none provided
    const defaultRenderResultItem = (result) => (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" component="span" fontWeight="bold">
                    {getResultValue(result)}
                </Typography>
                {result.type && (
                    <Typography
                        variant="caption"
                        component="span"
                        sx={{
                            fontWeight: 'bold',
                            textTransform: 'uppercase'
                        }}
                    >
                        {result.type}
                    </Typography>
                )}
            </Box>
            {result.name && (
                <Typography variant="body2" color="text.secondary">
                    {result.name}
                </Typography>
            )}
        </Box>
    );

    // Use provided renderer or fall back to default
    const finalRenderResultItem = renderResultItem || defaultRenderResultItem;

    // If search is hidden, don't render anything
    if (hideSearch) {
        return null;
    }

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box ref={searchRef} sx={{ position: 'relative', width: '100%' }}>
                <TextField
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={handleInputFocus}
                    variant="outlined"
                    size="small"
                    placeholder={isSelectionsAvailable() ? placeholder : `Maximum ${maxSelections} selections reached`}
                    fullWidth
                    autoComplete="off"
                    disabled={disabled || !isSelectionsAvailable()}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        )
                    }}
                />

                {/* Render dropdown in portal */}
                {portalContainerRef.current && (
                    <Portal container={portalContainerRef.current}>
                        {/* Loading indicator */}
                        {query.length > 0 && isLoading && showDropdown && (
                            <Paper
                                elevation={6}
                                sx={dropdownPaperStyle}
                            >
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                    <CircularProgress size={20} />
                                </Box>
                            </Paper>
                        )}

                        {/* No results message */}
                        {query.length > 0 && !isLoading && results.length === 0 && showDropdown && (
                            <Paper
                                elevation={6}
                                sx={dropdownPaperStyle}
                            >
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography color="text.secondary">
                                        No results found for "{query}"
                                    </Typography>
                                </Box>
                            </Paper>
                        )}

                        {/* Results dropdown */}
                        {results.length > 0 && showDropdown && (
                            <Paper
                                elevation={6}
                                sx={{
                                    ...dropdownPaperStyle,
                                    maxHeight: '400px',
                                    overflowY: 'auto'
                                }}
                            >
                                <List>
                                    {results.map((result) => (
                                        <ListItem
                                            button
                                            key={getResultKey(result)}
                                            onClick={() => handleResultClick(result)}
                                            sx={{
                                                '&:hover': {
                                                    backgroundColor: theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            {finalRenderResultItem(result)}
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        )}
                    </Portal>
                )}
            </Box>
        </ClickAwayListener>
    );
};

export default GenericSearchBar;